import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Box, 
  Paper, 
  Typography, 
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  Fab,
  SwipeableDrawer,
  Card,
  CardContent,
  Collapse,
  Badge,
  Button,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Star as StarIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Menu as MenuIcon
} from '@mui/icons-material';

import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import NotificationList from './NotificationList';

// Icons
import LabelIcon from '@mui/icons-material/Label';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

// 🆕 Mobile Page Info Drawer (slides from bottom)
const MobilePageInfoDrawer = ({ 
  isOpen, 
  onClose,
  userData,
  getUserInitials
}) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    help: false,
    ads: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <SwipeableDrawer
      anchor="bottom" // 🆕 Bottom sheet instead of right sidebar
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          height: '80vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          bgcolor: 'background.default'
        }
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: 40,
          height: 4,
          bgcolor: 'grey.300',
          borderRadius: 2,
          mx: 'auto',
          mt: 1,
          mb: 2
        }}
      />

      <Box sx={{ px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Page Information
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflow: 'auto', height: 'calc(80vh - 80px)' }}>
          {/* User Profile Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('profile')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Your Profile</Typography>
              </Box>
              {expandedSections.profile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.profile}>
              <CardContent sx={{ pt: 0, textAlign: 'center' }}>
                <Avatar
                  src={userData?.profileImageUrl}
                  alt={userData?.displayName}
                  sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                >
                  {getUserInitials(userData?.displayName)}
                </Avatar>
                
                <Typography variant="h6" gutterBottom>
                  {userData?.displayName || 'User Name'}
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <StarIcon sx={{ color: 'warning.main' }} fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Trust Score: {userData?.trustScore || 0}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    navigate('/profile');
                    onClose();
                  }}
                  sx={{ mt: 1 }}
                >
                  View Full Profile
                </Button>
                
                {/* Quick stats */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Account Activity
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {userData?.reviewCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reviews
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {userData?.networkSize || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Network
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {userData?.businessCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Businesses
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Collapse>
          </Card>

          {/* Help & Resources Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('help')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Help & Resources</Typography>
              </Box>
              {expandedSections.help ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.help}>
              <CardContent sx={{ pt: 0 }}>
                <List dense disablePadding>
                  <ListItem 
                    button 
                    component={Link} 
                    to="https://www.revfer.com/help" 
                    target="_blank"
                    sx={{ borderRadius: 1, mb: 1 }}
                    onClick={onClose}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Help Center" 
                      secondary="Get support and answers" 
                    />
                  </ListItem>
                  
                  <ListItem 
                    button 
                    component={Link} 
                    to="https://www.revfer.com/about" 
                    target="_blank"
                    sx={{ borderRadius: 1 }}
                    onClick={onClose}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PeopleOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="About Revfer" 
                      secondary="Learn more about our mission" 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Collapse>
          </Card>

          {/* Advertisement Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('ads')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LabelIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Sponsored</Typography>
              </Box>
              {expandedSections.ads ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.ads}>
              <CardContent sx={{ pt: 0 }}>
                <Box 
                  sx={{ 
                    width: '100%',
                    height: '150px',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    border: '1px dashed grey.300'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Advertisement
                  </Typography>
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Mobile Notification Stats Component - Uses real data from NotificationList logic
const MobileNotificationStats = ({ userData, notificationCounts }) => {
  const { totalCount = 0, unreadCount = 0 } = notificationCounts;

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        mb: 2, 
        border: 'none',
        bgcolor: 'primary.light',
        color: 'primary.contrastText'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={userData?.profileImageUrl}
            sx={{ width: 40, height: 40 }}
          >
            {userData?.displayName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ color: 'primary.contrastText' }}>
              {userData?.displayName || 'User'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ fontSize: 14, color: 'primary.contrastText', opacity: 0.8 }} />
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
                Trust Score: {userData?.trustScore || 0}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ textAlign: 'right' }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon sx={{ color: 'primary.contrastText' }} />
          </Badge>
          <Typography variant="caption" sx={{ display: 'block', color: 'primary.contrastText', opacity: 0.8 }}>
            {totalCount} total
          </Typography>
        </Box>
      </Box>
      
      {/* Notification Summary */}
      {totalCount > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
              Recent Activity
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {unreadCount > 0 && (
                <Chip
                  label={`${unreadCount} new`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'primary.contrastText',
                    fontSize: '0.75rem',
                    height: 20
                  }}
                />
              )}
              <Chip
                label={`${totalCount} total`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'primary.contrastText',
                  fontSize: '0.75rem',
                  height: 20
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// 🆕 Enhanced Mobile Header Component
const MobileNotificationHeader = ({ notificationCounts, onMenuClick }) => {
  const { unreadCount = 0 } = notificationCounts;

  return (
    <AppBar 
      position="fixed" 
      color="default" 
      elevation={0} 
      sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e0e0',
        top: '48px', // 🔧 FIXED: Position directly below the global header
        zIndex: 1100 // Ensure it's above content but below global header 
        }}>
      <Toolbar>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
        </Badge>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Notifications
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} new`}
              size="small"
              color="primary"
              sx={{ ml: 2, height: 22, fontSize: '0.75rem' }}
            />
          )}
        </Typography>
        <IconButton 
          color="primary" 
          onClick={onMenuClick}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

// 🆕 Main Component - Fixed mobile spacing
const Notifications = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 🆕 Bottom drawer state (no hamburger menu conflict)
  const [pageInfoOpen, setPageInfoOpen] = useState(false);

  const userData = useSelector((state) => state.auth?.user || null);

  // 🆕 Calculate notification counts - now handled by NotificationList callback
  const [notificationCounts, setNotificationCounts] = useState({
    totalCount: 0,
    unreadCount: 0
  });

  // 🆕 Callback to receive notification counts from NotificationList
  const handleNotificationCountChange = (counts) => {
    setNotificationCounts(counts);
  };

  // Function to get user initials for Avatar
  const getUserInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Box
      backgroundColor="#f5f5f5"
      maxWidth={1280}
      minHeight={'100vh'}
      mx="auto"
      sx={{ 
        // 🔧 FIXED: Corrected mobile top padding to match actual header height
        pt: isMobile ? '48px' : 2, // 48px matches the minHeight in Header.js for mobile
        pb: isMobile ? '80px' : 2, // Account for bottom navigation on mobile 
        px: isMobile ? 0 : 2 // No horizontal padding on mobile
      }}
    >
      {isMobile ? (
        // 🆕 Mobile Layout - Full screen
        <>
          {/* 🔧 FIXED: Added Mobile Page Header with hamburger menu */}
          <MobileNotificationHeader 
            notificationCounts={notificationCounts}
            onMenuClick={() => setPageInfoOpen(true)}
          />

          {/* Mobile Notification Stats - Add padding back to content */}
          <Box sx={{ p: 1 }}>
            <MobileNotificationStats 
              userData={userData} 
              notificationCounts={notificationCounts}
            />

            {/* Main Notification Feed */}
            <Paper
              variant="outlined"
              sx={{ 
                padding: '20px', 
                border: 'none', 
                mb: 1, 
                borderRadius: 2,
                minHeight: 'calc(100vh - 280px)' // 🔧 FIXED: Adjusted for page header + global header
              }}
            >
              <NotificationList 
                showHeader={false} 
                isMobile={true}
                onNotificationCountChange={handleNotificationCountChange}
              />
            </Paper>
          </Box>

          {/* 🆕 Pull-up indicator for additional info */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 100, // Above bottom navigation
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 999
            }}
          >
            <Fab
              color="primary"
              size="small"
              onClick={() => setPageInfoOpen(true)}
              sx={{
                bgcolor: 'rgba(25, 118, 210, 0.9)',
                '&:hover': {
                  bgcolor: 'primary.main'
                }
              }}
            >
              <KeyboardArrowUpIcon />
            </Fab>
          </Box>

          {/* 🆕 Bottom Sheet for Page Information */}
          <MobilePageInfoDrawer
            isOpen={pageInfoOpen}
            onClose={() => setPageInfoOpen(false)}
            userData={userData}
            getUserInitials={getUserInitials}
          />
        </>
      ) : (
        // 🆕 Desktop Layout - Original 3 Columns (unchanged)
        <Grid container spacing={2}>
          {/* Left Column - User Bio */}
          <Grid item xs={12} sm={3}>
            <Paper
              variant="outlined"
              sx={{ 
                padding: '20px', 
                border: 'none', 
                mb: 2, 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              {/* User Profile Section */}
              <Avatar
                src={userData?.profileImageUrl}
                alt={userData?.displayName}
                sx={{ width: 80, height: 80, mb: 2 }}
              >
                {getUserInitials(userData?.displayName)}
              </Avatar>
              
              <Typography variant="h6" gutterBottom align="center">
                {userData?.displayName || 'User Name'}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
                Trust Score: {userData?.trustScore || 0}
              </Typography>
            </Paper>
            
            <Paper
              variant="outlined"
              sx={{ padding: '20px', border: 'none', borderRadius: 2 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Help & Resources
              </Typography>
              
              <List dense disablePadding>
                <ListItem 
                  button 
                  component={Link} 
                  to="https://www.revfer.com/help" 
                  target="_blank"
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Help Center" 
                    secondary="Get support and answers" 
                  />
                </ListItem>
                
                <ListItem 
                  button 
                  component={Link} 
                  to="https://www.revfer.com/about" 
                  target="_blank"
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PeopleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="About Revfer" 
                    secondary="Learn more about our mission" 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          {/* Middle Column - Notification Feed */}
          <Grid item xs={12} sm={6}>
            <Paper
              variant="outlined"
              sx={{ 
                padding: '20px', 
                border: 'none', 
                mb: 1, 
                borderRadius: 2,
                minHeight: '70vh'
              }}
            >
              <NotificationList 
                showHeader={true} 
                isMobile={false}
                onNotificationCountChange={handleNotificationCountChange}
              />
            </Paper>
          </Grid>
          
          {/* Right Column - Ads & Recommendations */}
          <Grid item xs={12} sm={3}>
           <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                       <LabelIcon fontSize="small" color="action" />
                       <Typography variant="subtitle2" color="text.secondary">
                         Sponsored
                       </Typography>
                     </Box>
                     
                     {/* AdSense Placeholder */}
                     <Box 
                       sx={{ 
                         width: '100%',
                         height: '250px',
                         bgcolor: 'grey.100',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         borderRadius: 1,
                         border: '1px dashed grey.300'
                       }}
                     >
                       <Typography variant="body2" color="text.secondary">
                         Advertisement
                       </Typography>
                     </Box>
                   </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Notifications;